import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  GitCompareArrows,
  LayoutDashboard,
  Megaphone,
  Newspaper,
  ShieldCheck,
  Swords,
  Smartphone,
  Trophy,
  Zap,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { toast } from "sonner";

import AdminHeader from "@/components/admin/AdminHeader";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getAdminPlayoffSeriesResolutions, runAdminOfficialHeatsImport, runAdminOfficialMatchesImport } from "@/api/adminMatches";
import { getConfiguredSeason } from "@/lib/season";
import {
  createAnnouncement,
  deactivateAnnouncement,
  deleteAnnouncement,
  fetchAnnouncement,
  moveAnnouncement,
  updateAnnouncement,
} from "@/api/system";
import { formatSvDateTime, STOCKHOLM_TZ, toDate } from "@/utils/date";

const defaultForm = {
  id: null,
  title: "",
  message: "",
  level: "info",
  active: false,
  dismissible: true,
  expiresAt: "",
};

const levelOptions = [
  { value: "info", label: "Info" },
  { value: "success", label: "Succé" },
  { value: "warning", label: "Varning" },
  { value: "error", label: "Fel" },
];

const announcementPreviewClasses = {
  info: "border-sky-300/80 bg-sky-50/90 text-sky-950 dark:border-sky-400/30 dark:bg-sky-500/10 dark:text-sky-50",
  success:
    "border-emerald-300/80 bg-emerald-50/90 text-emerald-950 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-50",
  warning:
    "border-amber-300/80 bg-amber-50/90 text-amber-950 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-50",
  error: "border-red-300/80 bg-red-50/90 text-red-950 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-50",
};

function toLocalInputValue(iso) {
  if (!iso) return "";
  const date = toDate(iso);
  if (!date || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  try {
    const parts = new Intl.DateTimeFormat("sv-SE", {
      timeZone: STOCKHOLM_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value || "";
    return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
  } catch {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
}

function formatDateTime(iso) {
  if (!iso) return "-";
  return formatSvDateTime(iso, { dateStyle: "short", timeStyle: "short" }) || "-";
}

export default function AdminHomePage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";

  const [form, setForm] = useState(defaultForm);
  const [loadingAnnouncement, setLoadingAnnouncement] = useState(false);
  const [saving, setSaving] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [runningOfficialHeatsImport, setRunningOfficialHeatsImport] = useState(false);
  const [runningOfficialMatchesImport, setRunningOfficialMatchesImport] = useState(false);
  const [unresolvedPlayoffCount, setUnresolvedPlayoffCount] = useState(0);

  const statusBadge = useMemo(() => {
    const active = form.active;
    return {
      label: active ? "Aktivt" : "Avstängt",
      variant: active ? "default" : "secondary",
      className: active ? "bg-emerald-500 text-white hover:bg-emerald-500" : "",
    };
  }, [form.active]);

  const primaryActions = useMemo(
    () => [
      {
        to: "/admin/ads",
        label: "Hantera annonser",
        description: "Översikt och redigering av annonsytor.",
        icon: Newspaper,
      },
      {
        to: "/admin/ads/new",
        label: "Ny annons",
        description: "Skapa en ny annons direkt från admin.",
        icon: Megaphone,
      },
      ...(isSuper
        ? [
            {
              to: "/admin/guest-riders",
              label: "Inlånade förare",
              description: "Hantera matchspecifika inlåningar på en egen sida.",
              icon: LayoutDashboard,
            },
            {
              to: "/admin/notifications",
              label: "Skicka notis",
              description: "Skicka manuell notis till utvalda användare.",
              icon: Bell,
            },
            {
              to: "/admin/match-structure",
              label: "Matchstruktur",
              description: "Hantera säsongsstruktur, tabell och slutspel.",
              icon: Swords,
            },
            {
              to: "/admin/double-meeting-bonus",
              label: "Bonus dubbelmöten",
              description: "Hantera bonuspoäng och manuella avgöranden för dubbelmöten.",
              icon: GitCompareArrows,
            },
            {
              to: "/admin/app-releases",
              label: "Appuppdateringar",
              description: "Styr Flexible, Immediate och lägsta stödda Android-build.",
              icon: Smartphone,
            },
            {
              to: "/admin/playoff-series-resolutions",
              label: "Slutspelsavgöranden",
              description: unresolvedPlayoffCount
                ? `${unresolvedPlayoffCount} oavgjorda slutspelsserier kräver beslut.`
                : "Tilldela vinnare efter skiljeheat eller officiellt beslut.",
              icon: Trophy,
            },
          ]
        : []),
    ],
    [isSuper, unresolvedPlayoffCount]
  );

  useEffect(() => {
    if (!isSuper) return undefined;
    let active = true;
    getAdminPlayoffSeriesResolutions(getConfiguredSeason())
      .then((response) => {
        if (active) setUnresolvedPlayoffCount(Number(response?.unresolved_count || 0));
      })
      .catch(() => {});
    return () => { active = false; };
  }, [isSuper]);

  useEffect(() => {
    if (!isSuper) return;

    let mounted = true;
    setLoadingAnnouncement(true);
    fetchAnnouncement()
      .then((res) => {
        if (!mounted) return;
        setAnnouncements(Array.isArray(res?.announcements) ? res.announcements : []);
        setForm(defaultForm);
      })
      .catch((err) => {
        toast.error(err?.message || "Kunde inte hämta meddelandena");
      })
      .finally(() => {
        if (mounted) setLoadingAnnouncement(false);
      });

    return () => {
      mounted = false;
    };
  }, [isSuper]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    const trimmedTitle = form.title.trim();
    const trimmedMessage = form.message.trim();
    if (form.active && (!trimmedTitle || !trimmedMessage)) {
      toast.error("Titel och meddelande måste fyllas i för att kunna aktiveras.");
      return;
    }

    let expiresAtIso = null;
    if (form.expiresAt) {
      const dt = new Date(form.expiresAt);
      if (Number.isNaN(dt.getTime())) {
        toast.error("Ogiltigt utgångsdatum.");
        return;
      }
      if (form.active && dt.getTime() <= Date.now()) {
        toast.error("Utgångstiden måste ligga i framtiden för att meddelandet ska kunna aktiveras.");
        return;
      }
      expiresAtIso = dt.toISOString();
    }

    setSaving(true);
    try {
      const payload = {
        id: form.id,
        title: trimmedTitle,
        message: trimmedMessage,
        level: form.level,
        active: form.active,
        dismissible: form.dismissible,
        expiresAt: expiresAtIso,
      };
      const res = form.id ? await updateAnnouncement(payload) : await createAnnouncement(payload);
      setAnnouncements(Array.isArray(res?.announcements) ? res.announcements : []);
      setForm(defaultForm);
      toast.success(form.id ? "Meddelandet uppdaterades." : "Meddelandet skapades.");
    } catch (err) {
      toast.error(err?.message || "Kunde inte spara meddelandet");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (announcementId) => {
    if (saving) return;
    if (!window.confirm("Vill du ta bort meddelandet permanent?")) return;

    setSaving(true);
    try {
      const res = await deleteAnnouncement(announcementId);
      setAnnouncements(Array.isArray(res?.announcements) ? res.announcements : []);
      if (form.id === announcementId) setForm(defaultForm);
      toast.success("Meddelandet togs bort.");
    } catch (err) {
      toast.error(err?.message || "Kunde inte ta bort meddelandet");
    } finally {
      setSaving(false);
    }
  };

  const handleMove = async (announcementId, direction) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await moveAnnouncement(announcementId, direction);
      setAnnouncements(Array.isArray(res?.announcements) ? res.announcements : []);
    } catch (err) {
      toast.error(err?.message || "Kunde inte flytta meddelandet");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (announcementId) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await deactivateAnnouncement(announcementId);
      setAnnouncements(Array.isArray(res?.announcements) ? res.announcements : []);
      if (form.id === announcementId) setForm(defaultForm);
      toast.message("Meddelandet avaktiverades.");
    } catch (err) {
      toast.error(err?.message || "Kunde inte avaktivera meddelandet");
    } finally {
      setSaving(false);
    }
  };

  const handleEditAnnouncement = (announcement) => {
    setForm({
      id: announcement?.id || null,
      title: announcement?.title || "",
      message: announcement?.message || "",
      level: announcement?.level || "info",
      active: !!announcement?.active,
      dismissible: announcement?.dismissible !== false,
      expiresAt: toLocalInputValue(announcement?.expiresAt),
    });
  };

  const handleRunOfficialHeatsImport = async () => {
    if (runningOfficialHeatsImport) return;
    const confirmation = window.prompt("Skriv READY för att bekräfta import av official heats.");
    if (confirmation !== "READY") {
      if (confirmation !== null) toast.message("Importen avbröts. Skriv READY för att starta.");
      return;
    }
    setRunningOfficialHeatsImport(true);
    try {
      const res = await runAdminOfficialHeatsImport();
      toast.success(`Official heats-import klar. Hämtade: ${res?.fetched ?? 0}.`);
    } catch (err) {
      toast.error(err?.message || "Kunde inte köra official heats-import");
    } finally {
      setRunningOfficialHeatsImport(false);
    }
  };

  const handleRunOfficialMatchesImport = async () => {
    if (runningOfficialMatchesImport) return;
    const confirmation = window.prompt("Skriv READY för att bekräfta import av official matches.");
    if (confirmation !== "READY") {
      if (confirmation !== null) toast.message("Importen avbröts. Skriv READY för att starta.");
      return;
    }
    setRunningOfficialMatchesImport(true);
    try {
      const res = await runAdminOfficialMatchesImport();
      toast.success(
        `Official matches-import klar. Hämtade: ${res?.fetched ?? 0}, uppdaterade: ${res?.updated_existing ?? 0}.`
      );
    } catch (err) {
      toast.error(err?.message || "Kunde inte köra official matches-import");
    } finally {
      setRunningOfficialMatchesImport(false);
    }
  };

  return (
    <div className="min-h-screen">
      <AdminHeader />
      <main className="container mx-auto space-y-6 px-4 py-5 sm:py-6">
        <section className="grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(280px,0.8fr)]">
          <Card className="overflow-hidden border-border/70 shadow-sm">
            <CardHeader className="relative gap-4 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white">
              <div className="absolute inset-y-0 right-0 w-32 bg-[radial-gradient(circle_at_top,rgba(248,113,113,0.28),transparent_65%)]" />
              <div className="relative flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-xl text-white sm:text-2xl">
                    Välkommen {isSuper ? "Super Admin" : "Klubbadmin"}
                  </CardTitle>
                  <CardDescription className="mt-1 max-w-xl text-slate-200">
                    Adminöversikten samlar genvägar, importverktyg och globala meddelanden på ett ställe.
                  </CardDescription>
                </div>
              </div>
              <div className="relative grid gap-2 sm:grid-cols-3">
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300">Roll</div>
                  <div className="mt-1 text-sm font-semibold text-white">{isSuper ? "Super Admin" : "Klubbadmin"}</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300">Meddelanden</div>
                  <div className="mt-1 text-sm font-semibold text-white">{announcements.length} sparade</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300">Konto</div>
                  <div className="mt-1 truncate text-sm font-semibold text-white">{user?.email || "-"}</div>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="border-border/70 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <LayoutDashboard className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">Snabbinfo</CardTitle>
              </div>
              <CardDescription>Nuvarande konto och åtkomst i admin.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="rounded-xl border bg-card px-3 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Inloggad som</div>
                <div className="mt-1 break-all font-medium text-foreground">{user?.email}</div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                <div className="rounded-xl border bg-card px-3 py-3">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Roll</div>
                  <div className="mt-1 capitalize font-medium text-foreground">{user?.role}</div>
                </div>
                {user?.club_slug ? (
                  <div className="rounded-xl border bg-card px-3 py-3">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Klubb</div>
                    <div className="mt-1 font-medium uppercase text-foreground">{user.club_slug}</div>
                  </div>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </section>

        {isSuper && unresolvedPlayoffCount > 0 ? (
          <Card className="border-amber-500/40 bg-amber-500/5 shadow-sm">
            <CardContent className="flex flex-col gap-4 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-600"><Trophy className="h-4 w-4" /></div>
                <div><strong className="text-sm text-foreground">{unresolvedPlayoffCount} slutspelsavgörande{unresolvedPlayoffCount === 1 ? "" : "n"} väntar</strong><p className="mt-1 text-xs text-muted-foreground">Totalpoängen är lika. Registrera vinnaren efter skiljeheat eller officiellt beslut.</p></div>
              </div>
              <Button asChild><Link to="/admin/playoff-series-resolutions">Hantera avgöranden</Link></Button>
            </CardContent>
          </Card>
        ) : null}

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
          <Card className="border-border/70 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle>Snabbåtgärder</CardTitle>
              <CardDescription>Gå vidare till de vanligaste delarna i admin utan att leta i menyn.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2">
                {primaryActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <Link
                      key={action.to}
                      to={action.to}
                      className="group rounded-2xl border border-border/70 bg-card px-4 py-4 transition-colors hover:border-primary/40 hover:bg-muted/40"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-foreground">{action.label}</div>
                          <div className="mt-1 text-xs leading-relaxed text-muted-foreground">{action.description}</div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {isSuper ? (
            <Card className="border-border/70 shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-primary" />
                  <CardTitle>Importverktyg</CardTitle>
                </div>
                <CardDescription>
                  Manuella importer för officiella matcher och heat. Kräver bekräftelse med <span className="font-semibold text-foreground">READY</span>.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <button
                  type="button"
                  onClick={handleRunOfficialMatchesImport}
                  disabled={runningOfficialMatchesImport}
                  className="flex w-full items-start justify-between gap-3 rounded-2xl border border-border/70 bg-card px-4 py-4 text-left transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <div>
                    <div className="text-sm font-semibold text-foreground">Importera official matches</div>
                    <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      Hämtar officiella matcher och slutresultat från Flashscore-listan.
                    </div>
                  </div>
                  <Badge variant="secondary" className="shrink-0">
                    {runningOfficialMatchesImport ? "Kör..." : "Manuell"}
                  </Badge>
                </button>

                <button
                  type="button"
                  onClick={handleRunOfficialHeatsImport}
                  disabled={runningOfficialHeatsImport}
                  className="flex w-full items-start justify-between gap-3 rounded-2xl border border-border/70 bg-card px-4 py-4 text-left transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <div>
                    <div className="text-sm font-semibold text-foreground">Importera official heats</div>
                    <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      Kör heatimporten separat när officiella heat behöver hämtas om manuellt.
                    </div>
                  </div>
                  <Badge variant="secondary" className="shrink-0">
                    {runningOfficialHeatsImport ? "Kör..." : "Manuell"}
                  </Badge>
                </button>
              </CardContent>
            </Card>
          ) : null}
        </section>

        {isSuper ? (
          <Card className="border-border/70 shadow-sm">
            <CardHeader>
              <CardTitle>Bonuspoäng dubbelmöten</CardTitle>
              <CardDescription>Bonushanteringen ligger nu på en egen adminsida med tydligare struktur och kompaktare mobilvy.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <div className="text-sm font-medium text-foreground">Öppna bonussidan</div>
                <div className="text-sm text-muted-foreground">
                  Se olösta dubbelmöten, manuella avgöranden och hela bonusöversikten utan att belasta startsidan.
                </div>
              </div>
              <Button asChild>
                <Link to="/admin/double-meeting-bonus">Hantera bonuspoäng</Link>
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {isSuper ? (
          <Card className="border-border/70 shadow-sm">
            <CardHeader>
              <CardTitle>Matchinlåning</CardTitle>
              <CardDescription>
                Hantering av inlånade förare ligger på en egen adminsida med tydligare flöde för aktuella och tidigare tilldelningar.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <div className="text-sm font-medium text-foreground">Öppna gästförarsidan</div>
                <div className="text-sm text-muted-foreground">
                  Skapa, redigera och avsluta matchspecifika inlåningar utan att blanda ihop dem med övrig adminöversikt.
                </div>
              </div>
              <Button asChild>
                <Link to="/admin/guest-riders">Hantera inlåningar</Link>
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {isSuper ? (
          <Card className="border-border/70 shadow-sm">
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle>Globala meddelanden</CardTitle>
                  <Badge variant={statusBadge.variant} className={statusBadge.className}>
                    {statusBadge.label}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  {announcements.length ? `${announcements.length} meddelanden sparade` : "Inga meddelanden ännu"}
                </div>
              </div>
              <CardDescription>Skapa flera viktiga meddelanden som visas för alla inloggade användare.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingAnnouncement ? (
                <p className="text-sm text-muted-foreground">Hämtar aktuella meddelanden...</p>
              ) : (
                <>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="text-sm font-medium">{form.id ? "Redigera meddelande" : "Nytt meddelande"}</div>
                        <div className="text-xs text-muted-foreground">Titel visas alltid. Själva meddelandet kan expandera i appen.</div>
                      </div>
                      {form.id ? (
                        <Button type="button" variant="ghost" onClick={() => setForm(defaultForm)}>
                          Avbryt redigering
                        </Button>
                      ) : null}
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="announcement-title">Titel</Label>
                      <Input
                        id="announcement-title"
                        value={form.title}
                        onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                        placeholder="Kort rubrik för meddelandet"
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="announcement-message">Meddelande</Label>
                      <Textarea
                        id="announcement-message"
                        value={form.message}
                        onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
                        placeholder="Skriv meddelandet som ska visas i appen"
                        rows={4}
                      />
                    </div>

                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="grid gap-2">
                        <Label htmlFor="announcement-level">Nivå</Label>
                        <Select value={form.level} onValueChange={(value) => setForm((prev) => ({ ...prev, level: value }))}>
                          <SelectTrigger id="announcement-level">
                            <SelectValue placeholder="Välj typ av meddelande" />
                          </SelectTrigger>
                          <SelectContent>
                            {levelOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="announcement-expires">Utgångstid (valfri)</Label>
                        <Input
                          id="announcement-expires"
                          type="datetime-local"
                          value={form.expiresAt}
                          onChange={(event) => setForm((prev) => ({ ...prev, expiresAt: event.target.value }))}
                        />
                        <p className="text-xs text-muted-foreground">Lämna tomt för att låta meddelandet visas tills det stängs manuellt.</p>
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="flex items-center justify-between rounded-md border px-3 py-2">
                        <div>
                          <Label htmlFor="announcement-active" className="text-sm font-medium">Aktivera</Label>
                          <p className="text-xs text-muted-foreground">Styr om meddelandet visas för användarna.</p>
                        </div>
                        <Switch id="announcement-active" checked={form.active} onCheckedChange={(checked) => setForm((prev) => ({ ...prev, active: checked }))} />
                      </div>
                      <div className="flex items-center justify-between rounded-md border px-3 py-2">
                        <div>
                          <Label htmlFor="announcement-dismissible" className="text-sm font-medium">Kan stängas</Label>
                          <p className="text-xs text-muted-foreground">Om avstängd krävs manuell stängning av admin.</p>
                        </div>
                        <Switch
                          id="announcement-dismissible"
                          checked={form.dismissible}
                          onCheckedChange={(checked) => setForm((prev) => ({ ...prev, dismissible: checked }))}
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <Button type="submit" disabled={saving}>
                        {saving ? "Sparar..." : form.id ? "Uppdatera meddelande" : "Skapa meddelande"}
                      </Button>
                    </div>
                  </form>

                  <div className="space-y-3">
                    <div className="text-sm font-medium">Befintliga meddelanden</div>
                    {announcements.length ? (
                      announcements.map((announcement, index) => (
                        <div
                          key={announcement.id}
                          className={`rounded-xl border p-4 shadow-sm ${announcementPreviewClasses[announcement.level] || announcementPreviewClasses.info}`}
                        >
                          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-semibold">{announcement.title || "Utan titel"}</span>
                                <Badge variant={announcement.active ? "default" : "secondary"}>
                                  {announcement.active ? "Aktivt" : "Avstängt"}
                                </Badge>
                              </div>
                              <div className="mt-2 whitespace-pre-line text-sm leading-relaxed">{announcement.message}</div>
                              <div className="mt-3 text-xs opacity-80">
                                Uppdaterad: {formatDateTime(announcement.updatedAt)} • Version: {announcement.version ?? "-"}
                                {announcement.expiresAt ? ` • Utgår: ${formatDateTime(announcement.expiresAt)}` : ""}
                              </div>
                            </div>
                            <div className="flex shrink-0 flex-col gap-2 sm:min-w-[184px]">
                              <div className="flex items-center justify-start gap-2 sm:justify-end">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleMove(announcement.id, "up")}
                                  disabled={saving || index === 0}
                                  aria-label="Flytta upp"
                                >
                                  <ArrowUp className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  onClick={() => handleMove(announcement.id, "down")}
                                  disabled={saving || index === announcements.length - 1}
                                  aria-label="Flytta ned"
                                >
                                  <ArrowDown className="h-4 w-4" />
                                </Button>
                              </div>
                              <Button type="button" variant="outline" onClick={() => handleEditAnnouncement(announcement)}>
                                Redigera
                              </Button>
                              {announcement.active ? (
                                <Button type="button" variant="outline" onClick={() => handleDeactivate(announcement.id)} disabled={saving}>
                                  Stäng
                                </Button>
                              ) : null}
                              <Button type="button" variant="outline" onClick={() => handleDelete(announcement.id)} disabled={saving}>
                                Ta bort
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">Inga meddelanden skapade ännu.</p>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        ) : null}
      </main>
    </div>
  );
}
